// Sesión del organizador contra la API (Better Auth tras AuthPort, apps/api/src/auth). La cookie es httpOnly y del
// host de la API: la web nunca la ve; solo llama con `credentials: 'include'`.
// Enlace mágico en dos pasos (apps/api/src/auth/confirmLink.ts): el correo abre /ingresar/confirmar con el token en el
// fragmento, y recién el botón «Entrar» navega a la API, que gasta el token, deja la cookie y vuelve a callbackURL.

export interface Me {
  user: { id: string; email: string | null; name: string };
  accounts: { id: string; name: string; slug: string; role: 'owner' | 'admin' | 'editor' }[];
}
export interface EventRow { id: string; title: string; status: string; taken: number; capacity: number | null }

export class SessionApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) { super(message); this.name = 'SessionApiError'; this.status = status; }
}

export interface SessionApi {
  /** Pide el enlace. Responde igual exista o no el correo (la API no revela quién tiene cuenta). */
  requestLink(email: string, returnPath: string): Promise<void>;
  me(): Promise<Me | null>;
  events(accountId: string): Promise<EventRow[]>;
  signOut(): Promise<void>;
  /** URL de la API que gasta el token (la abre el botón «Entrar» con una navegación completa). */
  verifyUrl(p: ConfirmParams): string;
}

/** Solo rutas internas: `/app/…`. Cualquier otra cosa (otro origen, `//x`, esquemas) cae en /app. */
export function safeReturnPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/app';
  return raw.startsWith('/app') ? raw : '/app';
}

export interface ConfirmParams { token: string; callbackURL: string; errorCallbackURL: string }

/** Lee el fragmento del correo. Los destinos tienen que ser de esta misma web; si no, se ignoran. */
export function parseConfirmFragment(hash: string, origin: string): ConfirmParams | null {
  const q = new URLSearchParams(hash.replace(/^#/, ''));
  const token = q.get('token');
  if (!token) return null;
  const same = (u: string | null, fallback: string) => {
    if (!u) return fallback;
    try { const x = new URL(u, origin); return x.origin === origin ? x.toString() : fallback; } catch { return fallback; }
  };
  const callbackURL = same(q.get('callbackURL'), `${origin}/app`);
  return { token, callbackURL, errorCallbackURL: same(q.get('errorCallbackURL'), `${origin}/ingresar`) };
}

const MESSAGES: Record<number, string> = {
  403: 'Este sitio no puede pedir enlaces a la API. Avísanos si sigue pasando.',
  429: 'Pediste demasiados enlaces seguidos. Espera un minuto e inténtalo de nuevo.',
};

export function createSessionApi(baseUrl: string): SessionApi {
  const root = baseUrl.replace(/\/$/, '');
  const call = async (method: string, path: string, body?: unknown): Promise<Response> => {
    let res: Response;
    try {
      res = await fetch(root + path, {
        method, credentials: 'include',
        headers: { accept: 'application/json', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new SessionApiError(0, 'Sin conexión. Revisa tu red e inténtalo de nuevo.');
    }
    return res;
  };
  const fail = (res: Response) => new SessionApiError(res.status, MESSAGES[res.status] ?? 'No pudimos completar la acción. Inténtalo de nuevo.');
  return {
    async requestLink(email, returnPath) {
      const origin = window.location.origin;
      const res = await call('POST', '/auth/sign-in/magic-link', {
        email: email.trim().toLowerCase(),
        callbackURL: `${origin}${safeReturnPath(returnPath)}`,
        errorCallbackURL: `${origin}/ingresar`,
      });
      if (!res.ok) throw fail(res);
    },
    async me() {
      const res = await call('GET', '/v1/me');
      if (res.status === 401) return null;
      if (!res.ok) throw fail(res);
      return (await res.json()) as Me;
    },
    async events(accountId) {
      const res = await call('GET', `/v1/accounts/${encodeURIComponent(accountId)}/events`);
      if (!res.ok) throw fail(res);
      return ((await res.json()) as { events: EventRow[] }).events;
    },
    async signOut() {
      const res = await call('POST', '/auth/sign-out', {});
      if (!res.ok && res.status !== 401) throw fail(res);
    },
    verifyUrl(p) {
      const q = new URLSearchParams({ token: p.token, callbackURL: p.callbackURL, errorCallbackURL: p.errorCallbackURL });
      return `${root}/auth/magic-link/verify?${q}`;
    },
  };
}
